import { Controller } from "@hotwired/stimulus"

// Target-first column mapping (TASK-252.3; picker tagging + paged preview
// TASK-254). Chips are managed entirely client-side (add/remove + the
// hidden input that carries the column index); any change debounces a
// fetch to the `preview` action, which returns the preview panel, its
// badges/foot count AND every target's "Example:" line in one response —
// swapped in by plain element id (this is a manual fetch, not a
// turbo-frame navigation, so there's no frame to land in).
//
// The "+ Add column" pickers list every column, always (TASK-254) — no
// hiding. Every pick/remove re-tags every picker's options from the chips
// actually on the page, so a column already used by another target keeps
// showing which one(s), and re-picking a column already mapped to THIS
// target is inert (Andrew's ruling) rather than re-adding/duplicating it.
export default class extends Controller {
  static targets = ["headerRow", "dataStartRow", "targetRow", "chipRow", "columnMenu", "lookupButton"]
  static values = { previewUrl: String, limit: { type: Number, default: 20 } }

  connect() {
    this.abortController = null
    this.updateLookupButton()
    this.retagMenus()
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
  }

  headerChanged() {
    const headerVal = this.headerRowTarget.value
    if (headerVal !== "none") {
      const headerIdx = parseInt(headerVal, 10)
      const dataStart = parseInt(this.dataStartRowTarget.value, 10)
      if (dataStart <= headerIdx) {
        this.dataStartRowTarget.value = headerIdx + 1
      }
    }
    this.scheduleUpdate()
  }

  pick(event) {
    const button = event.currentTarget
    const index = parseInt(button.dataset.index, 10)
    const targetKey = button.dataset.targetKey
    const multi = button.dataset.multi === "true"
    const inputName = button.dataset.inputName
    const header = button.querySelector("span.truncate")?.textContent?.trim() || ""

    const chipRow = this.chipRowFor(targetKey)
    if (!chipRow) return

    if (chipRow.querySelector(`[data-chip-index="${index}"]`)) {
      // Already mapped to THIS target — inert (Andrew's ruling, TASK-254).
      return
    }

    if (!multi) {
      // Single-value targets replace rather than add.
      chipRow.querySelectorAll("[data-chip-index]").forEach((chip) => chip.remove())
    }

    chipRow.insertBefore(this.buildChip(index, header, inputName), chipRow.querySelector("[data-controller~='dropdown-popover']"))
    this.updateLookupButton()
    this.retagMenus()
    this.scheduleUpdate()
  }

  removeChip(event) {
    event.currentTarget.closest("[data-chip-index]")?.remove()
    this.updateLookupButton()
    this.retagMenus()
    this.scheduleUpdate()
  }

  showMore() {
    this.limitValue += 20
    this.scheduleUpdate()
  }

  scheduleUpdate() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchPreview(), 250)
  }

  async fetchPreview() {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set("header_row", this.hasHeaderRowTarget ? this.headerRowTarget.value : "1")
    url.searchParams.set("data_start_row", this.hasDataStartRowTarget ? this.dataStartRowTarget.value : "2")
    url.searchParams.set("limit", this.limitValue)

    this.element.querySelectorAll("input[name='name_columns[]']").forEach((input) => url.searchParams.append("name_columns[]", input.value))
    this.element.querySelectorAll("input[name='search_term_columns[]']").forEach((input) => url.searchParams.append("search_term_columns[]", input.value))

    const postcodeInput = this.element.querySelector("input[name='postcode_column']")
    if (postcodeInput) url.searchParams.set("postcode_column", postcodeInput.value)

    const clientUprnInput = this.element.querySelector("input[name='client_uprn_column']")
    if (clientUprnInput) url.searchParams.set("client_uprn_column", clientUprnInput.value)

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "text/html" },
        signal: this.abortController.signal
      })
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")

      doc.querySelectorAll("[id]").forEach((source) => {
        const target = document.getElementById(source.id)
        if (target) target.innerHTML = source.innerHTML
      })
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  // ── helpers ──────────────────────────────────────────────────────────

  chipRowFor(targetKey) {
    return this.chipRowTargets.find((row) => row.dataset.targetKey === targetKey)
  }

  updateLookupButton() {
    if (!this.hasLookupButtonTarget) return

    const nameRow = this.chipRowFor("site_name")
    const addressRow = this.chipRowFor("address")
    const hasName = nameRow && nameRow.querySelector("[data-chip-index]")
    const hasAddress = addressRow && addressRow.querySelector("[data-chip-index]")

    this.lookupButtonTarget.disabled = !(hasName && hasAddress)
  }

  // Rebuilds the "used by" tag on every column option in every picker from
  // the chips actually on the page right now. The server only computes
  // this once, at first render (_target_row.html.erb) — every pick/remove
  // after that is client-side only until the debounced preview fetch
  // lands, and that fetch never touches the pickers, so the tags have to
  // be kept live here instead.
  retagMenus() {
    const holders = {}
    this.targetRowTargets.forEach((row) => {
      const key = row.dataset.targetKey
      const label = row.dataset.targetLabel
      this.chipRowFor(key)?.querySelectorAll("[data-chip-index]").forEach((chip) => {
        const index = parseInt(chip.dataset.chipIndex, 10)
        if (!holders[index]) holders[index] = []
        holders[index].push({ key, label })
      })
    })

    this.columnMenuTargets.forEach((menu) => {
      const menuKey = menu.querySelector(".column-option")?.dataset.targetKey
      menu.querySelectorAll(".column-option").forEach((option) => {
        const index = parseInt(option.dataset.index, 10)
        const entries = holders[index] || []
        const mine = entries.find((entry) => entry.key === menuKey)
        const others = entries.filter((entry) => entry.key !== menuKey)
        const tag = option.querySelector(".column-option-tag")

        option.classList.toggle("bg-blue-50", !!mine)
        if (!tag) return

        if (mine) {
          tag.innerHTML = `<span class="text-[10px] bg-blue-100 text-blue-800 rounded px-1.5 py-0.5">In ${mine.label}</span>`
          tag.classList.remove("hidden")
        } else if (others.length) {
          tag.innerHTML = `<span class="text-[10px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">${others.map((entry) => entry.label).join(" &middot; ")}</span>`
          tag.classList.remove("hidden")
        } else {
          tag.innerHTML = ""
          tag.classList.add("hidden")
        }
      })
    })
  }

  buildChip(index, header, inputName) {
    const span = document.createElement("span")
    span.className = "inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-2 py-1 text-xs"
    span.dataset.chipIndex = String(index)

    const letter = document.createElement("span")
    letter.className = "font-mono font-bold text-blue-800"
    letter.textContent = this.columnLetter(index)

    const label = document.createElement("span")
    label.className = "text-gray-700"
    label.textContent = header || "(no header)"

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = inputName
    input.value = String(index)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "text-gray-400 hover:text-gray-600"
    remove.setAttribute("aria-label", `Remove ${header || `column ${this.columnLetter(index)}`}`)
    remove.innerHTML = "&times;"
    remove.addEventListener("click", () => {
      span.remove()
      this.updateLookupButton()
      this.retagMenus()
      this.scheduleUpdate()
    })

    span.append(letter, label, input, remove)
    return span
  }

  columnLetter(index) {
    let letter = ""
    let n = index
    for (;;) {
      letter = String.fromCharCode(65 + (n % 26)) + letter
      n = Math.floor(n / 26) - 1
      if (n < 0) break
    }
    return letter
  }
}
